import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Field = ({ label, value, mono, wide }) => (
  <div className={`${wide ? "col-md-12" : "col-md-3 col-6"} mb-3`}>
    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: "#1a1f36", fontFamily: mono ? "monospace" : "inherit", fontWeight: mono ? 600 : 400, whiteSpace: "pre-wrap" }}>{value || "—"}</div>
  </div>
);

const Section = ({ title, icon, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border rounded">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB", borderRadius: "4px 4px 0 0" }}
        onClick={() => setOpen(!open)}>
        <span><i className={`bi ${icon} me-2`}></i>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-0 p-3">{children}</div>}
    </div>
  );
};

const STATUS_COLOR = { OPEN: "#dc3545", IN_PROGRESS: "#fd7e14", CLOSED: "#198754" };
const STATUS_BG = { OPEN: "#fff5f5", IN_PROGRESS: "#fffbeb", CLOSED: "#f0fdf4" };

export default function ViewMaintenance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [activeTab, setActiveTab] = useState("systemNotes");

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

  useEffect(() => {
  if (!token || !id) return;
  setLoading(true);
  fetch(`${API_BASE_URL}/api/asset-maintenance/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
    .then(setRecord)
    .catch(() => { toast.error("Failed to load maintenance record."); navigate("/allmaintenance"); })
    .finally(() => setLoading(false));
}, [id, token, navigate]);

  const handleStatusUpdate = async (status) => {
    setUpdating(status);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      const msg = status === "CLOSED" ? "Maintenance closed. Asset restored to Available." : "Marked as In Progress.";
      toast.success(msg);
      setRecord(prev => ({ ...prev, status }));
    } catch { toast.error("Failed to update status."); }
    finally { setUpdating(null); }
  };

  if (loading) return (
    <>
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid" style={{ paddingTop: 110 }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading maintenance record...</div>
        </div>
      </div>
    </>
  );

  if (!record) return null;

  const status = record.status || "OPEN";

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="fw-bold mb-0"><i className="bi bi-tools me-2"></i>Maintenance Detail</h4>
              <span className="badge" style={{ background: STATUS_BG[status], color: STATUS_COLOR[status], border: `1px solid ${STATUS_COLOR[status]}30`, fontSize: 12 }}>
                {status}
              </span>
            </div>
            <div className="d-flex gap-2 mt-1">
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/editmaintenance/${record.id}`)}>
                 Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                 Back
              </button>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {status !== "CLOSED" && (
              <>
                {status === "OPEN" && (
                  <button className="btn btn-warning btn-sm" onClick={() => handleStatusUpdate("IN_PROGRESS")} disabled={!!updating}>
                    {updating === "IN_PROGRESS" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-gear me-1"></i>}
                    In Progress
                  </button>
                )}
                <button className="btn btn-success btn-sm" onClick={() => handleStatusUpdate("CLOSED")} disabled={!!updating}>
                  {updating === "CLOSED" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check-circle me-1"></i>}
                  Close
                </button>
              </>
            )}
            {record.assetId && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/viewasset/${record.assetId}`)}>
                <i className="bi bi-box-seam me-1"></i> View Asset
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/allmaintenance")}>
              <i className="bi bi-list-ul me-1"></i> All Maintenance
            </button>
          </div>
        </div>

        <Section title="Asset" icon="bi-laptop">
          <Field label="Asset Code" value={record.assetCode} mono />
          <Field label="Asset Name" value={record.assetName} />
          {/* <Field label="Category" value={record.category} />
          <Field label="Location" value={record.location} /> */}
        </Section>

        <Section title="Maintenance Info" icon="bi-tools">
          <Field label="Maintenance Type" value={record.maintenanceType} />
          <Field label="Service Date" value={record.serviceDate} />
          <Field label="Vendor" value={record.vendorName} />
          <Field label="SLA Target (days)" value={record.slaTarget} />
          <Field label="Repair Cost" value={record.repairCost ? `₹${Number(record.repairCost).toLocaleString("en-IN")}` : null} />
          <Field label="Parts Replaced" value={record.partsReplaced} />
          <Field label="Issue Description" value={record.issueDescription} wide />
          <Field label="Work Done" value={record.workDone} wide />
        </Section>

        {record.closedAt && (
          <Section title="Closure" icon="bi-check-circle">
            <Field label="Closed At" value={record.closedAt ? new Date(record.closedAt).toLocaleString() : null} />
            <Field label="Closed By" value={record.closedBy} />
          </Section>
        )}

        {/* System Information Tabs */}
        <ul className="nav nav-tabs mb-0 fw-semibold mt-3" style={{ backgroundColor: navbarColor }}>
          {[["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3">
          <div className="text-muted small mb-2">
            <strong>Date Created:</strong>{" "}
            {record.createdAt ? new Date(record.createdAt).toLocaleString() : "—"}
          </div>
          {activeTab === "systemNotes" && (
            <SystemNotes entityType="MAINTENANCE" entityId={record.id} token={token} />
          )}
        </div>
      </div>
    </>
  );
}
